import {
  getUrlHeaders,
  getVideoVersions,
  getPdfByUrl,
  isOnline
} from "@/services/http/httpService.js";
import {store} from "@/store";

export function hasCachedObject(key, storeName) {
  return self.cache.hasCachedObject(key, storeName);
}

export async function deleteCachedElementsForLanguageInDB(language) {
  const { deleteObject } = self.cache;
  const videos = { ...store.getters.getForCertainLanguage(language) };
  const pdfs = { ...store.getters.getPdfsForCertainLanguage(language) };
  const numberOfElementsToDelete =
    Object.keys(videos).length + Object.keys(pdfs).length;
  store.commit("setIsDeletingElements", true);
  store.commit("setSumOfElementsToDelete", numberOfElementsToDelete);
  console.log(
    `[Caching] begin deleting of ${numberOfElementsToDelete} objects for language ${language}`
  );
  for (let video in videos) {
    const key = videos[video].path;
    await deleteObject(key, "videos");
    store.commit("incrementDeletedElements");
  }
  for (let pdf in pdfs) {
    const key = pdfs[pdf].path;
    await deleteObject(key, "pdfs");
    store.commit("incrementDeletedElements");
  }
  console.log(
    `[Caching] finished deleting of ${numberOfElementsToDelete} objects for language ${language}`
  );
  setTimeout(() => {
    store.dispatch("setCachingStatus", ["de", "en"]);
    store.commit("resetDeletedElementsAndSumOdElements");
    store.commit("setIsDeletingElements", false);
  }, 1000);
}

export async function getPdfByURL(key) {
  const { hasCachedObject, getValueFromDb } = self.cache;
  var hasCache = await hasCachedObject(key, "pdfs");
  if (hasCache) {
    return getValueFromDb(key, "pdfs");
  }
}

export async function checkAllElementsForUpdate() {
  const online = await isOnline();
  if (!online) {
    return;
  }
  const allElements = store.getters.getAllElements;
  for (let i in allElements) {
    const element = allElements[i];
    const isVideo = element.elementtype === "videos";
    const isPdf = element.elementtype === "pdfs";
    const elementNeedsUpdate = await checkIfCachedElementNeedsToBeUpdated(
      element
    );
    if (elementNeedsUpdate) {
      console.log(
        `[Caching] ${element.value.path} is outdated and will be updated`
      );
      if (isVideo) {
        cacheVideo(element.value);
      }
      if (isPdf) {
        cachePdf(element.value);
      }
    }
  }
}

export async function getAll(videos, pdfs) {
  store.commit("setIsCachingElements", true);
  try {
    const { hasCachedObject } = self.cache;
    for (let index = 0; index < videos.length; index++) {
      const video = videos[index];
      const videoIsCached = await hasCachedObject(video.path, "videos");
      const videoIsValid = videoIsCached
        ? await checkIfFileIsValid(video.path, "videos")
        : true;
      if (!videoIsCached || !videoIsValid) {
        await cacheVideo(video);
      }
      store.commit("incrementLoadedElements");
    }
    for (let index = 0; index < pdfs.length; index++) {
      const pdf = pdfs[index];
      const pdfIsCached = await hasCachedObject(pdf.path, "pdfs");
      const pdfIsValid = pdfIsCached
        ? await checkIfFileIsValid(pdf.path, "pdfs")
        : true;
      if (!pdfIsCached || !pdfIsValid) {
        await cachePdf(pdf);
      }
      store.commit("incrementLoadedElements");
    }
  } catch (e) {
    store.commit("setCachingError", true);
  }
  setTimeout(() => {
    store.dispatch("setCachingStatus", ["de", "en"]);
    store.commit("setIsCachingElements", false);
    store.commit("resetLoadedElementsAndSumOfElements");
  }, 1000);
}

async function checkIfCachedElementNeedsToBeUpdated({ value, elementtype }) {
  const { hasCachedObject, getObjectHeaders } = self.cache;
  const isCached = await hasCachedObject(value.path, elementtype);
  if (!isCached) {
    return false;
  }
  try {
    const isVideo = elementtype === "videos";
    const isPdf = elementtype === "pdfs";
    let modifyingDate = null;
    if (isVideo) {
      const response = await getVideoVersions(value.vimeoId);
      if (!response || !response.modifyingDate) {
        return false;
      }
      modifyingDate = Date.parse(response.modifyingDate);
    }
    if (isPdf) {
      const response = await getUrlHeaders(value.url);
      if (!response) {
        return false;
      }
      const headers = parseStringOfHeadersIntoArray(response);
      modifyingDate = getModifyingDateValue(headers, "date");
    }
    if (modifyingDate === null) {
      return false;
    }
    const cachedHeaders = await getObjectHeaders(value.path, elementtype);
    const cachingDate = getModifyingDateValue(cachedHeaders, "date");
    const needsUpdate = modifyingDate > cachingDate;
    return needsUpdate;
  } catch (e) {
    return false;
  }
}

async function checkIfFileIsValid(path, elementtype) {
  const { getObjectHeaders } = self.cache;
  const cachedHeaders = await getObjectHeaders(path, elementtype);
  const contentType = await findHeader(cachedHeaders, "content-type");
  if (
    (contentType.value == "video/mp4" && elementtype == "videos") ||
    (contentType.value == "application/pdf" && elementtype == "pdfs")
  ) {
    return true;
  }
  return false;
}

function findHeader(headers, key) {
  return headers.find(element => {
    return element.key && element.key.toLowerCase() === key.toLowerCase();
  });
}

function getModifyingDateValue(headers, key) {
  const header = findHeader(headers, key);
  if (header && header.value) {
    return Date.parse(header.value);
  }
  return null;
}

function parseStringOfHeadersIntoArray(response) {
  let headers = [];
  response.data.split("\n").forEach(element => {
    let splitetdElement = element.split(":");
    headers.push({ key: splitetdElement[0], value: splitetdElement[1] });
  });

  return headers;
}

async function cacheVideo({ path, vimeoId }) {
  try {
    const { cacheResponseToIdb } = self.cache;
    const { downloadUrl } = await getVideoVersions(vimeoId);
    console.log(`[Caching] downloading video: ${vimeoId} from ${downloadUrl}`);
    const response = await fetch(downloadUrl);
    await cacheResponseToIdb(path, response, "videos");
    document.dispatchEvent(
      new CustomEvent("videodownloaded", {
        detail: { vimeoId }
      })
    );
    return {
      success: true
    };
  } catch (e) {
    const { deleteObject } = self.cache;
    await deleteObject(path, "videos");
    handleError(e);
  }
}

async function cachePdf({ path, url }) {
  try {
    const { cacheResponseToIdb } = self.cache;
    console.log(`[Caching] downloading document: ${path} from ${url}`);
    const response = await getPdfByUrl(url);
    const result = await cacheResponseToIdb(path, response, "pdfs");
    return result;
  } catch (e) {
    const { deleteObject } = self.cache;
    await deleteObject(path, "pdfs");
    handleError(e);
  }
}

function handleError(e) {
  if (e.name === "TypeError" && e.message === "Failed to fetch") {
    throw new Error("typeError_failed_to_fetch");
  } else {
    throw e;
  }
}
